import {
  captureError,
  getDialect,
  getDefaultDialect,
  getCurrentLang,
  generateCharacterChatURL,
} from "@/utils/common.helpers";
import { API_ROUTES } from "../apiRoutes";
import { REQUEST_METHODS } from "../configs.apiClient";
import { HttpClient } from "../HttpClient";
import type {
  Character,
  ChatMessageType,
  GetCharactersByContentResponse,
  GetCharactersByShowResponse,
  GetConversationsResponse,
  GetMessagesResponse,
  OpenChatResponse,
  SendMessageResponse,
  StreamMessageEvent,
} from "@/types/chat.types";

// 60s overall stream timeout: generous enough to allow the server to begin
// streaming for slow-starting responses, while still bounding worst-case hangs.
const STREAM_TIMEOUT_MS = 60_000;

// 30s per-read timeout: guards against stalled mid-stream connections where
// the server stops sending data without closing the stream.
const READ_TIMEOUT_MS = 30_000;

export class ChatAPIClient {
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  async openChat({
    characterSlug,
  }: {
    characterSlug: string;
  }): Promise<OpenChatResponse> {
    try {
      const response = await this.httpClient.nestRequest<OpenChatResponse>({
        endpoint: API_ROUTES.chat.open,
        options: { method: REQUEST_METHODS.POST },
        body: { characterSlug },
        addToken: true,
      });

      return response;
    } catch (error) {
      throw captureError(error);
    }
  }

  async sendMessage({
    characterSlug,
    message,
    type = "text",
  }: {
    characterSlug: string;
    message: string;
    type?: ChatMessageType;
  }): Promise<SendMessageResponse> {
    try {
      const response = await this.httpClient.nestRequest<SendMessageResponse>({
        endpoint: API_ROUTES.chat.send,
        options: { method: REQUEST_METHODS.POST },
        body: { characterSlug, message, type },
        addToken: true,
      });

      return response;
    } catch (error) {
      throw captureError(error);
    }
  }

  streamMessage({
    characterSlug,
    message,
    onMessage,
    onError,
    onComplete,
    onDone,
  }: {
    characterSlug: string;
    message: string;
    onMessage: (event: StreamMessageEvent) => void;
    onError: (error: Error) => void;
    onComplete: () => void;
    onDone?: (event: StreamMessageEvent) => void;
  }): () => void {
    const abortController = new AbortController();

    (async () => {
      let streamTimeoutId: ReturnType<typeof setTimeout> | null = null;
      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

      try {
        streamTimeoutId = setTimeout(() => {
          abortController.abort();
        }, STREAM_TIMEOUT_MS);

        const response = await this.httpClient.nestStreamRequest({
          endpoint: API_ROUTES.chat.stream,
          queryParams: {
            characterSlug,
            message,
          },
          addToken: true,
          signal: abortController.signal,
        });

        reader = response.body?.getReader() ?? null;
        if (!reader) {
          throw new Error("No response body");
        }

        const decoder = new TextDecoder();
        let buffer = "";

        const readWithTimeout = async (): Promise<
          ReadableStreamReadResult<Uint8Array>
        > => {
          let readTimeoutId: ReturnType<typeof setTimeout> | null = null;
          try {
            return await Promise.race([
              reader!.read(),
              new Promise<never>((_, reject) => {
                readTimeoutId = setTimeout(
                  () =>
                    reject(
                      new Error(
                        `Chat stream read timeout: No data received for ${READ_TIMEOUT_MS / 1000}s (character: ${characterSlug})`,
                      ),
                    ),
                  READ_TIMEOUT_MS,
                );
              }),
            ]);
          } finally {
            if (readTimeoutId) clearTimeout(readTimeoutId);
          }
        };

        let isStreamComplete = false;
        while (!isStreamComplete) {
          const { done, value } = await readWithTimeout();

          if (done) {
            isStreamComplete = true;
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data: StreamMessageEvent = JSON.parse(line.slice(6));
                onMessage(data);

                if (data.done) {
                  onDone?.(data);
                  isStreamComplete = true;
                  break;
                }
              } catch (error) {
                captureError(error);
              }
            }
          }
        }

        onComplete();
      } catch (error) {
        if ((error as { name?: string })?.name === "AbortError") {
          return;
        }
        captureError(error);
        onError(error as Error);
      } finally {
        if (streamTimeoutId) clearTimeout(streamTimeoutId);
        if (reader) {
          try {
            await reader.cancel();
          } catch (error) {
            try {
              const errorMessage = error instanceof Error ? error.message : "";
              const isExpectedError =
                errorMessage.includes("already been consumed") ||
                errorMessage.includes("already closed") ||
                errorMessage.includes("locked") ||
                errorMessage.includes("released");

              if (!isExpectedError) {
                captureError(error);
              }
            } catch {
              // Defensive: never let logging break cleanup
            }
          }
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }

  async getMessages({
    characterSlug,
    limit = 20,
    before,
  }: {
    characterSlug: string;
    limit?: number;
    before?: string;
  }): Promise<GetMessagesResponse> {
    try {
      const queryParams: Record<string, string> = {
        characterSlug,
        limit: String(limit),
      };
      if (before) queryParams.before = before;

      const response = await this.httpClient.nestRequest<GetMessagesResponse>({
        endpoint: API_ROUTES.chat.messages,
        options: { method: REQUEST_METHODS.GET },
        queryParams,
        addToken: true,
      });

      return response;
    } catch (error) {
      throw captureError(error);
    }
  }

  async getConversations(): Promise<GetConversationsResponse> {
    try {
      const response =
        await this.httpClient.nestRequest<GetConversationsResponse>({
          endpoint: API_ROUTES.chat.conversations,
          options: { method: REQUEST_METHODS.GET },
          addToken: true,
        });

      return response;
    } catch (error) {
      throw captureError(error);
    }
  }

  async getCharactersByShow({
    contentSlug,
    contentType,
  }: {
    contentSlug: string;
    contentType: string;
  }): Promise<Character[]> {
    try {
      const response =
        await this.httpClient.nestRequest<GetCharactersByShowResponse>({
          endpoint: API_ROUTES.chat.getCharactersByShow(contentSlug),
          options: { method: REQUEST_METHODS.GET },
          addToken: false,
        });

      const lang = await getCurrentLang();
      const dialect = (await getDialect()) || getDefaultDialect();

      return response.characters.map((character) => {
        const characterSlug =
          character.slug || (character as { id?: string }).id || "";

        return {
          ...character,
          slug: characterSlug,
          showSlug: contentSlug,
          show: response.show?.title || contentSlug,
          characterUrl: generateCharacterChatURL({
            lang,
            dialect,
            contentType,
            contentSlug,
            characterSlug,
          }),
        } as Character;
      });
    } catch (error) {
      throw captureError(error);
    }
  }

  async getCharactersByContent({
    contentSlug,
    contentType,
    token,
  }: {
    contentSlug: string;
    contentType: string;
    token?: string;
  }): Promise<Character[]> {
    try {
      const response =
        await this.httpClient.nestRequest<GetCharactersByContentResponse>({
          endpoint: API_ROUTES.chat.characters,
          options: { method: REQUEST_METHODS.GET },
          queryParams: { contentSlug, contentType },
          addToken: !!token,
          token,
        });

      const lang = await getCurrentLang();
      const dialect = (await getDialect()) || getDefaultDialect();

      return response.characters.map((character) => ({
        ...character,
        showSlug: contentSlug,
        show: contentSlug,
        characterUrl: generateCharacterChatURL({
          lang,
          dialect,
          contentType,
          contentSlug,
          characterSlug: character.slug,
        }),
      }));
    } catch (error) {
      throw captureError(error);
    }
  }
}
